import { type ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'Button';
import { Text } from 'Text';
import FrontRoyalSpinner from 'FrontRoyalSpinner';

// An "Upload" button backed by a hidden file input, plus the in-progress spinner and any error
// message. Shared by the transcript and English-proficiency sections, which both upload immediately
// on selection rather than as part of a form submit (as the old ngf-select inputs did).
export function DocumentUploadButton({
    accept,
    isDisabled,
    uploading,
    errorMessage,
    onFileSelected,
    children,
}: {
    accept: string;
    isDisabled: boolean;
    uploading: boolean;
    errorMessage: string | null;
    onFileSelected: (file: File) => void;
    // Rendered between the button and the error message (the uploaded-document list).
    children?: ReactNode;
}) {
    const { t } = useTranslation('back_royal');
    const fileInputRef = useRef<HTMLInputElement>(null);

    return (
        <div className="flex flex-col gap-2">
            {/* `hidden` keeps this out of the tab order. Matches AvatarUpload and FileInput. */}
            <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept={accept}
                data-testid="document-file-input"
                onChange={event => {
                    const file = event.target.files?.[0];
                    // Cleared so re-selecting the same file still fires a change event.
                    event.target.value = '';
                    if (file) onFileSelected(file);
                }}
            />

            {/* The list sits beside the button on wide screens and above it on narrow ones, where
                there's no room alongside. `items-start` keeps the button level with the first row. */}
            <div className="flex flex-col-reverse gap-4 sm:flex-row sm:items-start sm:gap-6">
                <div className="flex items-center gap-3">
                    <Button
                        type="button"
                        color="primary"
                        height={9}
                        className="w-fit shrink-0 px-3"
                        isDisabled={isDisabled}
                        onPress={() => fileInputRef.current?.click()}
                    >
                        {t('settings.edit_documents.upload')}
                    </Button>

                    {uploading ? <FrontRoyalSpinner className="no-delay inline" /> : null}
                </div>

                {children}
            </div>

            {errorMessage ? (
                <Text as="div" color="red" className="text-xs">
                    {errorMessage}
                </Text>
            ) : null}
        </div>
    );
}
